import React from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Chips } from 'primereact/chips';
import { ModernButton } from './ui/ModernButton';
import { CustomerStatus, CustomerSource } from '../types/agent';

// Styles for these class names live in src/pages/agent/AddCustomerPage.css,
// which both the Add and Edit customer pages already import.

export interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: CustomerStatus;
  source: CustomerSource;
  leadScore: number;
  notes: string;
  tags: string[];
  income?: number;
  creditScore?: number;
  employmentStatus?: string;
  homeOwnershipStatus?: string;
  location?: string;
  mortgageBalance?: number;
  homeValue?: number;
}

export const emptyCustomerFormData: CustomerFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  status: 'prospect',
  source: 'other',
  leadScore: 50,
  notes: '',
  tags: [],
  employmentStatus: '',
  homeOwnershipStatus: '',
  location: ''
};

export const customerStatusOptions: { label: string; value: CustomerStatus }[] = [
  { label: 'Prospect', value: 'prospect' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Converted', value: 'converted' }
];

export const customerSourceOptions: { label: string; value: CustomerSource }[] = [
  { label: 'Mobile App', value: 'app' },
  { label: 'Website', value: 'website' },
  { label: 'Referral', value: 'referral' },
  { label: 'Import', value: 'import' },
  { label: 'Other', value: 'other' }
];

export const validateCustomerForm = (formData: CustomerFormData): Record<string, string> => {
  const newErrors: Record<string, string> = {};

  if (!formData.firstName.trim()) {
    newErrors.firstName = 'First name is required';
  }

  if (!formData.lastName.trim()) {
    newErrors.lastName = 'Last name is required';
  }

  if (!formData.email.trim()) {
    newErrors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    newErrors.email = 'Invalid email format';
  }

  if (formData.phone && !/^[\d\s\-+()]+$/.test(formData.phone)) {
    newErrors.phone = 'Invalid phone number format';
  }

  if (formData.creditScore !== undefined && (formData.creditScore < 300 || formData.creditScore > 850)) {
    newErrors.creditScore = 'Credit score must be between 300 and 850';
  }

  return newErrors;
};

interface CustomerFormProps {
  formData: CustomerFormData;
  errors: Record<string, string>;
  onFieldChange: (field: keyof CustomerFormData, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  loading: boolean;
  submitLabel: string;
  submitIcon: string;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
  formData,
  errors,
  onFieldChange,
  onSubmit,
  onCancel,
  loading,
  submitLabel,
  submitIcon
}) => {
  return (
    <form onSubmit={onSubmit}>
      <div className="form-grid">
        {/* Basic Information */}
        <Card className="form-section">
          <h3>Basic Information</h3>

          <div className="form-row">
            <div className="field">
              <label htmlFor="firstName">First Name *</label>
              <InputText
                id="firstName"
                value={formData.firstName}
                onChange={(e) => onFieldChange('firstName', e.target.value)}
                className={errors.firstName ? 'p-invalid' : ''}
                placeholder="John"
              />
              {errors.firstName && <small className="p-error">{errors.firstName}</small>}
            </div>

            <div className="field">
              <label htmlFor="lastName">Last Name *</label>
              <InputText
                id="lastName"
                value={formData.lastName}
                onChange={(e) => onFieldChange('lastName', e.target.value)}
                className={errors.lastName ? 'p-invalid' : ''}
                placeholder="Doe"
              />
              {errors.lastName && <small className="p-error">{errors.lastName}</small>}
            </div>
          </div>

          <div className="form-row">
            <div className="field">
              <label htmlFor="email">Email *</label>
              <InputText
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => onFieldChange('email', e.target.value)}
                className={errors.email ? 'p-invalid' : ''}
                placeholder="john.doe@example.com"
              />
              {errors.email && <small className="p-error">{errors.email}</small>}
            </div>

            <div className="field">
              <label htmlFor="phone">Phone</label>
              <InputText
                id="phone"
                value={formData.phone}
                onChange={(e) => onFieldChange('phone', e.target.value)}
                className={errors.phone ? 'p-invalid' : ''}
                placeholder="(555) 123-4567"
              />
              {errors.phone && <small className="p-error">{errors.phone}</small>}
            </div>
          </div>

          <div className="field">
            <label htmlFor="location">Location</label>
            <InputText
              id="location"
              value={formData.location || ''}
              onChange={(e) => onFieldChange('location', e.target.value)}
              placeholder="City, State"
            />
          </div>
        </Card>

        {/* Lead Information */}
        <Card className="form-section">
          <h3>Lead Information</h3>

          <div className="form-row">
            <div className="field">
              <label htmlFor="status">Status</label>
              <Dropdown
                id="status"
                value={formData.status}
                options={customerStatusOptions}
                onChange={(e) => onFieldChange('status', e.value)}
                placeholder="Select Status"
              />
            </div>

            <div className="field">
              <label htmlFor="source">Lead Source</label>
              <Dropdown
                id="source"
                value={formData.source}
                options={customerSourceOptions}
                onChange={(e) => onFieldChange('source', e.value)}
                placeholder="Select Source"
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="leadScore">Lead Score (0-100)</label>
            <div className="lead-score-controls">
              <input
                type="number"
                id="leadScore"
                value={formData.leadScore}
                onChange={(e) => onFieldChange('leadScore', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                min={0}
                max={100}
                className="lead-score-input"
              />
              <div className="slider-container">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={formData.leadScore}
                  onChange={(e) => onFieldChange('leadScore', parseInt(e.target.value))}
                  className="lead-score-slider"
                />
                <div className="slider-labels">
                  <span>0</span>
                  <span>50</span>
                  <span>100</span>
                </div>
              </div>
            </div>
          </div>

          <div className="field">
            <label htmlFor="tags">Tags</label>
            <Chips
              id="tags"
              value={formData.tags}
              onChange={(e) => onFieldChange('tags', e.value || [])}
              placeholder="Add tags..."
            />
            <small>Press Enter to add tags</small>
          </div>
        </Card>

        {/* Notes */}
        <Card className="form-section full-width">
          <h3>Additional Notes</h3>
          <div className="field">
            <InputTextarea
              id="notes"
              value={formData.notes}
              onChange={(e) => onFieldChange('notes', e.target.value)}
              rows={4}
              placeholder="Add any additional notes about this customer..."
              style={{ width: '100%' }}
            />
          </div>
        </Card>
      </div>

      <div className="modern-form-actions">
        <ModernButton
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </ModernButton>
        <ModernButton
          type="submit"
          variant="primary"
          icon={submitIcon}
          loading={loading}
          disabled={loading}
        >
          {submitLabel}
        </ModernButton>
      </div>
    </form>
  );
};

export default CustomerForm;
